// Alleen sessiegebonden app-verzoeken mogen een verlopen app-sessie afsluiten.
// Deze uitzonderingen volgen de publieke/machine-routes van proxy.js.
export const NON_SESSION_API_PREFIXES = Object.freeze([
  '/api/auth/', '/api/public/',
  '/api/assistant/mcp', '/api/assistant/oauth/register', '/api/assistant/oauth/token',
  '/api/preview-isolation',
  '/api/notify',
  '/api/kpi-notify',
  '/api/voip-sync',
  '/api/daily-report',
  '/api/weekly-report',
  '/api/monitor',
  '/api/version',
  '/api/cron/',
  '/api/calls-sync',
  '/api/morning-digest',
  '/api/opvolging-alert',
  '/api/applicants/sync',
  '/api/applicants/webhook',
  '/api/site-visits',
  '/api/geo',
  '/api/sourcing/dedupe',
  '/api/sourcing/candidate',
  '/api/sourcing/call-lists',
  '/api/sourcing/contact',
  '/api/sourcing/ping',
  '/api/sourcing/meta',
  '/api/sourcing/company-check',
  '/api/sourcing/update-employer',
  '/api/sourcing/update-fields',
  '/api/sourcing/reconcile',
  '/api/sourcing/quick',
  '/api/sourcing/record',
  '/api/sourcing/feed',
  '/api/sourcing/job-watch',
  '/api/sourcing/inmail-sync',
  '/api/sourcing/functies',
  '/api/sourcing/email-lookup',
  '/api/sourcing/clip-job',
  '/api/sourcing/log-mail',
  '/api/sourcing/disconnect',
  '/api/gdpr/accept',
  '/api/gdpr/respond',
  '/api/gdpr/statement',
  '/api/gdpr/webhook',
  '/api/email-inbound',
  '/api/voip/webhook',
  '/api/m365/notify',
  '/api/unsubscribe',
  '/api/dealtune/pending',
  '/api/dealtune/ring',
  '/api/dealtune/audio',
  '/api/dealtune/board',
  '/api/dealtune/avatar',
  '/api/dealtune/slide-image',
  '/api/company-logo',
  '/api/dev/login',
]);

export function isSessionApiRequest(input, init, location) {
  if (location.pathname !== '/') return false;
  if ((init?.credentials ?? input?.credentials) === 'omit') return false;
  try {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
    return url.origin === location.origin && url.pathname.startsWith('/api/')
      && !NON_SESSION_API_PREFIXES.some(prefix => url.pathname.startsWith(prefix));
  } catch { return false; }
}

const SESSION_NOTICE_KEY = 'pb-session-expired';
export const SESSION_EXPIRED_NOTICE = 'Sessie verlopen. Log opnieuw in.';

export function rememberSessionExpiry(target) {
  try { target.sessionStorage.setItem(SESSION_NOTICE_KEY, '1'); return true; }
  catch { return false; }
}

export function consumeSessionExpiryNotice(target) {
  let expired = false;
  try {
    expired = target.sessionStorage.getItem(SESSION_NOTICE_KEY) === '1';
    target.sessionStorage.removeItem(SESSION_NOTICE_KEY);
  } catch { /* Opslag kan door browserbeleid geblokkeerd zijn. */ }
  try {
    const url = new URL(target.location.href);
    if (url.searchParams.get('session') === 'expired') {
      expired = true;
      url.searchParams.delete('session');
      target.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
  } catch { /* De melding blijft bruikbaar zonder History API. */ }
  return expired ? SESSION_EXPIRED_NOTICE : '';
}

export function expiredSessionHref(location, remembered = true) {
  return ('/login' + (remembered ? '' : '?session=expired')) + (String(location.hash || '').startsWith('#') ? location.hash : '');
}

// Eén waarnemer voor fetch, ook voor niet-SWR zoekers en schrijfacties. De
// originele Response en fout blijven intact; uitloggen gebeurt hooguit één keer.
export function installSessionExpiryGuard(target, onExpired) {
  const original = target.fetch;
  let active = true, expired = false;
  const guarded = async function (...args) {
    const response = await original.apply(this, args);
    if (active && !expired && response.status === 401 && isSessionApiRequest(args[0], args[1], target.location)) {
      let localResponse = true;
      try { if (response.url) localResponse = new URL(response.url).origin === target.location.origin; } catch { localResponse = false; }
      if (localResponse) { expired = true; onExpired(); }
    }
    return response;
  };
  target.fetch = guarded;
  return () => {
    active = false;
    if (target.fetch === guarded) target.fetch = original;
  };
}
